import React, { memo, useCallback, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import Select from '@atlaskit/select';

import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import PresenceUnavailableIcon from '@atlassian/jira-icons/src/ui/presence-unavailable/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { RequestType } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import type { FormatOptionLabelMeta } from '@atlassian/jira-issue-field-select-base/src/common/types.tsx';
import { preventEscKeyPropagationKeyPressHandler } from '../../../../../common/utils/index.tsx';
import messages from './messages.tsx';
import type { Props, NoneOption } from './types.tsx';

const selectStyles = {
	// @ts-expect-error - TS7006 - Parameter 'supplied' implicitly has an 'any' type.
	control: (supplied) => ({
		...supplied,
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
	}),
	// @ts-expect-error - TS7006 - Parameter 'supplied' implicitly has an 'any' type.
	valueContainer: (supplied) => ({
		...supplied,
		padding: '0',
		paddingLeft: token('space.150'),
		height: '42px', // specify a static height for the container, because the text container is absolutely positioned
	}),
	// @ts-expect-error - TS7006 - Parameter 'supplied' implicitly has an 'any' type.
	menuList: (supplied) => ({
		...supplied,
		padding: 0,
	}),
	// @ts-expect-error - TS7006 - Parameter 'supplied' implicitly has an 'any' type.
	group: (supplied) => ({
		...supplied,
		padding: 0,
	}),
	// @ts-expect-error - TS7006 - Parameter 'supplied' implicitly has an 'any' type.
	groupHeading: (supplied) => ({
		...supplied,
		margin: 0,
		paddingTop: token('space.200'),
		paddingBottom: token('space.100'),
		font: token('font.heading.xsmall'),
		color: token('color.text'),
		textTransform: 'none', // default is uppercase
	}),
	// @ts-expect-error - TS7006 - Parameter 'supplied' implicitly has an 'any' type.
	singleValue: (supplied) => ({
		...supplied,
		top: 0,
		left: token('space.150'),
		textOverflow: 'ellipsis',
		transform: 'none', // default is -50% y
		whiteSpace: 'nowrap',
		width: '100%',
		paddingRight: token('space.400'),
	}),
	// @ts-expect-error - TS7006 - Parameter 'supplied' implicitly has an 'any' type.
	option: (supplied) => ({
		...supplied,
		cursor: 'pointer',
		paddingLeft: token('space.200'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		margin: 0,
	}),
} as const;

export const RequestTypeSelect = memo((props: Props) => {
	const { formatMessage } = useIntl();

	const formatOptionLabel = useCallback(
		(
			option: NoneOption | RequestType,
			{ context }: FormatOptionLabelMeta<NoneOption | RequestType>,
		) => {
			const description =
				option.type === 'NONE'
					? formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.noRequestTypeOptionDescriptionIssueTermRefresh
								: messages.noRequestTypeOptionDescription,
						)
					: option.description;

			return (
				<FlexRow>
					<IconWrapper>
						{option.type === 'NONE' ? (
							<PresenceUnavailableIcon label="" />
						) : (
							Boolean(option.iconUrl) && <Icon src={option.iconUrl} alt={option.label} />
						)}
					</IconWrapper>
					<FlexColumn isMenu={context === 'menu'}>
						<Label
							data-test-id="issue-create.ui.modal.create-form.type-picker.request-type-select.label"
							isMenu={context === 'menu'}
						>
							{option.label}
						</Label>
						<Description
							data-test-id="issue-create.ui.modal.create-form.type-picker.request-type-select.description"
							isMenu={context === 'menu'}
						>
							{description}
						</Description>
					</FlexColumn>
				</FlexRow>
			);
		},
		[formatMessage],
	);
	return (
		// @ts-expect-error - TS2322 - Type '{ isClearable: false; formatOptionLabel: (option: NoneOption | RequestType, { context }: FormatOptionLabelMeta<NoneOption | RequestType>) => Element; ... 10 more ...;
		<Select
			{...props}
			isClearable={false}
			formatOptionLabel={formatOptionLabel}
			hideSelectedOptions={false}
			placeholder=""
			maxMenuHeight={256}
			styles={selectStyles}
			onKeyDown={preventEscKeyPropagationKeyPressHandler}
			aria-label={formatMessage(messages.ariaLabel)}
		/>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexRow = styled2.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	alignItems: 'flex-start',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FlexColumn = styled.div<{
	children?: ReactNode;
	isMenu: boolean;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
}>((props) => ({
	flexDirection: 'column',
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `calc(100% - ${props.isMenu ? 40 : 14}px)`,
}));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled2.div<{
	children?: ReactNode;
}>({
	marginRight: token('space.200'),
	width: '24px',
	height: '24px',
});

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Label = styled.p<{
	children?: ReactNode;
	isMenu: boolean;
	['data-test-id']: string;
}>`
	font: ${token('font.heading.xsmall')};
	color: ${token('color.text')};
	margin-top: ${token('space.200')};
	height: 18px;
	width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Icon = styled2.img<{
	src: string;
	alt: string;
}>({
	height: '24px',
	width: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled2.p<{
	children?: ReactNode;
	isMenu: boolean;
	['data-test-id']: string;
}>({
	// height: 20px; - maintain a consistent height for the case when there is no description
	height: '20px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtle'),
	marginTop: 0,
	paddingTop: token('space.050'),
	font: token('font.body'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	width: '100%',
});
